// src/utils/exportUtils.js
export const generateCsvData = (scenarioResults, scenarios, globalSettings) => {
  // Check for missing essential data
  if (!scenarioResults || !scenarios || !globalSettings) {
    console.error("CSV Export: Missing essential data (results, scenarios, or settings).");
    return 'Year,Error\n,Missing data for export'; // Return basic error CSV
  }

  // Build headers and data based on visible scenarios
  const headers = ['Year'];
  
  // Add columns for each visible scenario
  const visibleScenarios = scenarios.filter(s => s.visible);

  // Check if there are any visible scenarios with results
  if (visibleScenarios.length === 0) {
    console.warn("CSV Export: No visible scenarios to export.");
    return 'Year\nNo visible scenarios'; // Return only header or a message
  }

  // Check if results exist for at least one visible scenario
  const firstVisibleScenarioWithResults = visibleScenarios.find(s => scenarioResults[s.id] && !scenarioResults[s.id].error);
  if (!firstVisibleScenarioWithResults) {
      console.warn("CSV Export: No calculation results found for any visible scenarios.");
      return 'Year\nNo results for visible scenarios'; // Return only header or a message
  }
  const firstScenarioId = firstVisibleScenarioWithResults.id;
  const firstResults = scenarioResults[firstScenarioId];

  if (!firstResults || !firstResults.years || firstResults.years.length === 0) {
    console.warn("CSV Export: No years data found in the first available scenario results.");
    return 'Year\nNo years data found'; // Return only header or a message
  }

  // Build Headers dynamically based on available data and settings
  visibleScenarios.forEach(scenario => {
    const results = scenarioResults[scenario.id];
    if (results && !results.error) { // Only add headers if results exist and no error
      if (results.salaryData) headers.push(`${scenario.name} - Salary`);
      if (results.salaryData && globalSettings.showNpv) headers.push(`${scenario.name} - Salary NPV`);
      
      if (results.pensionData) headers.push(`${scenario.name} - Pension`);
      if (results.pensionData && globalSettings.showNpv) headers.push(`${scenario.name} - Pension NPV`);
      
      if (results.tspBalanceData) headers.push(`${scenario.name} - TSP Withdrawal`);
      if (results.tspBalanceData && globalSettings.showNpv) headers.push(`${scenario.name} - TSP Withdrawal NPV`);

      if (results.tspBalanceData) headers.push(`${scenario.name} - TSP Balance`);

      if (results.supplementData) headers.push(`${scenario.name} - FERS Supplement`);
      if (results.supplementData && globalSettings.showNpv) headers.push(`${scenario.name} - FERS Supplement NPV`);

      if (results.socialSecurityData) headers.push(`${scenario.name} - Social Security`);
      if (results.socialSecurityData && globalSettings.showNpv) headers.push(`${scenario.name} - Social Security NPV`);

      headers.push(`${scenario.name} - Total Income`);
      if (globalSettings.showNpv) headers.push(`${scenario.name} - Total NPV`);
    }
  });

  const years = firstResults.years; // Use years from the first valid scenario
  const rows = years.map((year, index) => {
    const row = [year];
    let columnIndex = 1;
    visibleScenarios.forEach(scenario => {
      const results = scenarioResults[scenario.id];
      if (results && !results.error) {
        // Salary
        if (results.salaryData) row[columnIndex++] = results.salaryData[index] || 0;
        if (results.salaryData && globalSettings.showNpv) row[columnIndex++] = results.npvData?.salaryNpvData?.[index] || 0;
        
        // Pension
        if (results.pensionData) row[columnIndex++] = results.pensionData[index] || 0;
        if (results.pensionData && globalSettings.showNpv) row[columnIndex++] = results.npvData?.pensionNpvData?.[index] || 0;
        
        // TSP Withdrawal & Balance
        if (results.tspBalanceData) {
          const tspWithdrawal = results.tspWithdrawalData?.[index] || 0;
          row[columnIndex++] = tspWithdrawal;
          if (globalSettings.showNpv) row[columnIndex++] = results.npvData?.tspWithdrawalNpvData?.[index] || 0;
          row[columnIndex++] = results.tspBalanceData[index] || 0;
        }

        // FERS Supplement
        if (results.supplementData) row[columnIndex++] = results.supplementData[index] || 0;
        if (results.supplementData && globalSettings.showNpv) row[columnIndex++] = results.npvData?.supplementNpvData?.[index] || 0;

        // Social Security
        if (results.socialSecurityData) row[columnIndex++] = results.socialSecurityData[index] || 0;
        if (results.socialSecurityData && globalSettings.showNpv) row[columnIndex++] = results.npvData?.socialSecurityNpvData?.[index] || 0;

        // Total Income & NPV
        row[columnIndex++] = results.totalIncomeData?.[index] || 0;
        if (globalSettings.showNpv) row[columnIndex++] = results.npvData?.totalNpvData?.[index] || 0;
      } else {
        // Fill empty columns if a scenario has no results or an error
        // This requires knowing how many columns *should* have been added
        // For simplicity, we can skip or add placeholders. Skipping might be safer.
      }
    });
    return row;
  });
  
  // Add Total NPV row if applicable
  if (globalSettings.showNpv) {
    const totalRow = new Array(headers.length).fill('');
    totalRow[0] = 'Total NPV';
    
    let columnIndex = 1;
    visibleScenarios.forEach(scenario => {
      const results = scenarioResults[scenario.id];
      if (results && !results.error) {
        // Determine number of columns added for this scenario based on headers
        let colsForScenario = 0;
        if (results.salaryData) colsForScenario += (1 + (globalSettings.showNpv ? 1 : 0));
        if (results.pensionData) colsForScenario += (1 + (globalSettings.showNpv ? 1 : 0));
        if (results.tspBalanceData) colsForScenario += (1 + (globalSettings.showNpv ? 1 : 0) + 1); // Withdrawal + NPV + Balance
        if (results.supplementData) colsForScenario += (1 + (globalSettings.showNpv ? 1 : 0));
        if (results.socialSecurityData) colsForScenario += (1 + (globalSettings.showNpv ? 1 : 0));
        colsForScenario += (1 + (globalSettings.showNpv ? 1 : 0)); // Total Income + Total NPV

        // Place the total NPV in the last column for this scenario's block
        const totalNpvIndex = columnIndex + colsForScenario - 1;
        if (totalNpvIndex < totalRow.length) {
            totalRow[totalNpvIndex] = results.npvData?.totalNpv || 0;
        }
        columnIndex += colsForScenario; // Move to the start of the next scenario block
      } 
    });
    
    rows.push(totalRow);
  }
  
  // Convert to CSV
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  return csvContent;
};

export const downloadCsv = (csvContent, filename = 'federal-benefits-projection.csv') => {
  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};